/* eslint-disable react-hooks/exhaustive-deps */
import React, {Suspense, useState} from 'react';
import {useSelector} from 'react-redux';
import omit from 'lodash.omit';
import {useTranslation} from 'react-i18next';
import {
  AutoFillArgs,
  AutoFillResponse,
  ComputedPageItem,
  ReducerState,
} from '@taopix/taopix-core/dist/types/types';
import {
  ViewMode,
  AutoFlowError,
  SERVER_ERROR_CODE,
  AssistantType,
  AIMode,
  DetectionType,
  CollectionType,
} from '@taopix/taopix-core';
import {LoadingOverlay} from '@taopix/taopix-design-system';
import classNames from 'classnames';
import {DragAndDropNames} from '../../../../Enums';
import useSettings from '../../../../Hooks/useSettings';
import {LayoutSelector, AssetServicesSelector, UserAssetSelector} from '../../../../Selectors';
import {AssetServicesActions} from '../../../Context/AssetServices/AssetServicesActions';
import {useAssetServices} from '../../../Context/AssetServices/AssetServicesContext';
import {ProjectCacheProvider} from '../../../Context/Projects/ProjectsContext';
import {useTaopix} from '../../../Context/TaopixContext';
import {UserFeedbackActions} from '../../../Context/UserFeedback/UserFeedbackActionTypes';
import {useUserFeedback} from '../../../Context/UserFeedback/UserFeedbackContext';
import {UserInterfaceActions} from '../../../Context/UserInterface/UserInterfaceActionTypes';
import {useUiState} from '../../../Context/UserInterface/UserInterfaceContext';
import Drag from '../../../DragAndDrop/Drag';
import {useExperience} from '../../../Experience/ExperienceProvider';
import PageActionsPalette from '../../../Page/PageActionsPalette';
import BackgroundsPalette from '../../../Palette/BackgroundsPalette';
import BoxSettingsPalette from '../../../Palette/BoxSettings/BoxSettingsPalette';
import CalendarPalette from '../../../Palette/Calendars/CalendarPalette';
import DevPalette from '../../../Palette/Dev/DevPalette';
import FlightCheckPalette from '../../../Palette/FlightCheck/FlightCheckPalette';
import LayoutsPalette from '../../../Palette/LayoutsPalette';
import SettingsPalette from '../../../Palette/SettingsPalette';
import ShapeSettings from '../../../Palette/ShapeSettings';
import StickerPalette from '../../../Palette/StickerPalette';
import ThemesPalette from '../../../Palette/ThemesPalette';
import SmartDesignButton from '../../../Sidebar/SmartDesignButton';
import ToolPaletteIntroTip from '../../../Sidebar/ToolPaletteIntroTip';
import ToolPalette from '../../../ToolPalette/ToolPalette';
import {PageActionsProvider} from '../../../Context/PageActions/PageActionsContext';
import AddPicturesButton from '../../../AssetServices/AddPicturesButton';
import {useRegisterKeyboardCommand} from '../../../Context/KeyboardCommandsContext';
import AccessibleTextEditingPalette from '../../../Palette/AccessibleTextEditingPalette';

export type SidebarProps = {
  selectedItem: ComputedPageItem;
  viewMode: ViewMode;
};

const PicturePalette = React.lazy(() => import('../../../Palette/PicturePalette'));

const Sidebar = ({selectedItem: item, viewMode}: SidebarProps) => {
  // grab the app state
  const app = useTaopix();
  const settings = useSettings();
  const ui = useUiState();
  const userFeedback = useUserFeedback();
  const [showLoadingOverlay, setShowLoadingOverlay] = useState<boolean>(false);
  const [imagesLoading, setImagesLoading] = useState(false);
  const {t} = useTranslation();

  // pull in the experience settings
  const {experience, getAIMode} = useExperience();
  const assistantType = experience.WIZARD.designAssistantType.designAssistantType;
  const {toolPalette} = experience.EDITOR;
  const services = useAssetServices();

  // get the layout from the store
  const productLayout = useSelector(LayoutSelector);

  // get the asset services
  const assetServices = useSelector(AssetServicesSelector);

  // get some props from the core
  const server = app.core.get('server');

  const sectionPath = ui.state.palette.section;
  const isOpen = null !== sectionPath;
  const aiMode = getAIMode();

  // grab the list of user asset from the store
  const assets = useSelector(UserAssetSelector);

  useRegisterKeyboardCommand({
    code: 'Digit2',
    shiftKey: true,
    callback: () => {
      const selector =
        ui.state.palette.section !== null
          ? '.tpx-palette-section--selected button:not(.tpx-palette-close-button):not([disabled]), .tpx-palette-section--selected textarea:not([disabled])'
          : '#tool-palette-container button';
      const sidebarButtons = document.querySelectorAll(selector);
      (sidebarButtons?.[0] as HTMLElement)?.focus();
    },
  });

  const onCloseAssetServiceList = () => {
    services.dispatch(
      AssetServicesActions.setOpenState({
        ...omit({...services.state}, ['assets', 'serviceDialogOpen']),
        open: false,
      })
    );
  };

  // build up the default props for each palette section
  const paletteDefaultProps = {
    item: item,
  };

  const groupOneClassNames = classNames(
    'flex',
    'space-y-sm',
    'flex-col',
    'transition-[padding]',
    'duration-standard',
    'lg:pt-md',
    isOpen ? 'lg:px-sm' : 'lg:px-md'
  );

  //prettier-ignore
  const popoutClassNames = classNames(
    // On small screen, the popout content will have a min width of 300rem or the screen bounds as it will be a modal
    'sm:min-w-[min(theme(width.bounds),300rem)]',
  );

  const onAutoFill = () => {
    if (experience.EDITOR.pictures.showAdvancedAutofill) {
      ui.dispatch(
        UserInterfaceActions.setAutoFillOptions({
          ...ui.state.autoFillOptions,
          autoFillDialog: true,
        })
      );
    } else {
      setShowLoadingOverlay(true);
      app.core
        .get('stateManager')
        .call<AutoFillArgs, ReducerState & AutoFillResponse>('autofill', {
          save: true,
          showLoading: true,
          sortOrder: ui.state.palette.picturePallette.sortOrder,
          sortDirection: ui.state.palette.picturePallette.sortDirection,
          includeCovers: true,
          checkImageUsage: false,
          fromCreation: false,
          createPreviewThumbnail: true,
          coverItems: [],
        })
        .then(() => {
          setShowLoadingOverlay(false);
          ui.dispatch(
            UserInterfaceActions.setAutoFillOptions({
              autoFillCompleted: true,
            })
          );
        })
        .catch(error => {
          setShowLoadingOverlay(false);
          userFeedback.dispatch(
            UserFeedbackActions.setErrorUserFeedback({
              title: '',
              message:
                error instanceof AutoFlowError ? error.message : server.getErrorFromResponse(error),
              showHeader: SERVER_ERROR_CODE !== error.response?.status,
            })
          );
        });
    }
  };

  const showAddPicturesButton = toolPalette.showAddPicturesButton && !ui.state.isSmallScreen;
  const showSmartDesignButton =
    assistantType === AssistantType.SmartDesign &&
    toolPalette.showSmartDesignButton &&
    aiMode > AIMode.NotAvailable;

  return (
    <>
      <ToolPalette isOpen={isOpen}>
        {(showAddPicturesButton || showSmartDesignButton) && (
          <div id="tool-palette__group-one" className={groupOneClassNames} role="presentation">
            {showAddPicturesButton && (
              <Suspense>
                <AddPicturesButton
                  close={onCloseAssetServiceList}
                  onLoading={setImagesLoading}
                  popoutUsesParentWidth={!isOpen}
                  buttonProps={{
                    hideLabel: ui.state.palette.section !== null,
                    id: 'mainAddPicturesButton',
                    arrow: ui.state.palette.section === null ? 'down' : undefined,
                  }}
                  popoutProps={{
                    className: popoutClassNames,
                    displayMode: ui.state.isSmallScreen ? 'modal' : 'positioned',
                  }}
                />
              </Suspense>
            )}
            {showSmartDesignButton && (
              <SmartDesignButton toolPaletteOpen={isOpen} onAutoFill={onAutoFill} />
            )}
          </div>
        )}

        <div
          id="tool-palette__group-two"
          className="flex sm:items-start lg:mt-md lg:flex-col lg:overflow-auto"
          role="tablist"
          aria-label={t('toolpalettes:str_LabelToolPalettes', {defaultValue: 'Tool Palettes'})}
          aria-orientation={ui.state.isSmallScreen ? 'horizontal' : 'vertical'}
        >
          <Drag
            name={DragAndDropNames.PAGE}
            detectionType={DetectionType.AutoLayout}
            item={{}}
            passive={true}
          >
            <PicturePalette
              assetServices={assetServices}
              pictureUsage={
                Array.isArray(productLayout?.usagepicture) ? {} : productLayout?.usagepicture
              }
              sectionKey={'pictures'}
              authentication={settings.state.authentication}
              authenticationPrompt={() => {}}
              designerSettings={settings.state}
              selectableImages={false}
              server={server}
              assets={assets}
              isLoading={imagesLoading}
              onAutoFill={onAutoFill}
              {...paletteDefaultProps}
            />
          </Drag>

          <LayoutsPalette sectionKey={'layouts'} viewMode={viewMode} {...paletteDefaultProps} />

          <BackgroundsPalette
            sectionKey={'backgrounds'}
            viewMode={viewMode}
            {...paletteDefaultProps}
          />

          <StickerPalette sectionKey={'stickers'} {...paletteDefaultProps} />

          {settings.state.productTheming.enabled &&
            CollectionType.Calendar !== productLayout.type && (
              <ProjectCacheProvider>
                <ThemesPalette sectionKey={'themes'} {...paletteDefaultProps} />
              </ProjectCacheProvider>
            )}

          <ShapeSettings sectionKey={'shape-options'} {...paletteDefaultProps} />

          {productLayout.type === CollectionType.Calendar && (
            <CalendarPalette
              sectionKey={'calendar-palette'}
              assets={assets}
              {...paletteDefaultProps}
            />
          )}

          {!productLayout.retroprints && (
            <BoxSettingsPalette
              sectionKey={'box-settings-palette'}
              {...paletteDefaultProps}
              assets={assets}
            />
          )}

          <FlightCheckPalette sectionKey={'flight-check'} {...paletteDefaultProps} />

          <AccessibleTextEditingPalette
            sectionKey={'accessible-text-editing-palette'}
            {...paletteDefaultProps}
          />

          <PageActionsProvider>
            <PageActionsPalette />
          </PageActionsProvider>

          <ToolPaletteIntroTip />
        </div>

        <div
          className="sm:flex shrink-0 lg:mt-auto lg:mb-sm"
          role="tablist"
          aria-orientation="vertical"
        >
          {app.state.isDev && <DevPalette sectionKey="dev" {...paletteDefaultProps} />}
          <SettingsPalette sectionKey={'project-settings'} {...paletteDefaultProps} />
        </div>
      </ToolPalette>
      <LoadingOverlay
        id={'sidebar-autoFill-busy-overlay'}
        open={showLoadingOverlay}
        label={t('*:str_MessagePleaseWait')}
        delay={500}
        key={'sidebar-autoFill-busy-overlay'}
      />
    </>
  );
};

export default Sidebar;
